<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { storeToRefs } from "pinia"
import { useRouter } from "vue-router"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"

defineOptions({
    name: "HomeBuildingActionsDialog",
})

// ================================
// TYPES
// ================================
type BuildingOptionCard = {
    label: string
    iconType: string
    onSelect: () => void
}

// ================================
// COMPOSABLES
// ================================
const homeWorkspace = useHomeWorkspaceStore()
const { modal } = storeToRefs(homeWorkspace)
const router = useRouter()

// ================================
// INTERACTIONS
// ================================
/**
 * Ferme le dialogue des parcours d’ajout de bâtiment.
 */
function onClose(): void {
    homeWorkspace.closeModal()
}

/**
 * Redirige vers l’ajout simple de bâtiment.
 */
function onGoSimple(): void {
    onClose()
    router.push({ path: "/onboarding/building" })
}

/**
 * Redirige vers l’ajout multi-compteurs.
 */
function onGoBatch(): void {
    onClose()
    router.push({ path: "/onboarding/append-multi-meters" })
}

/**
 * Redirige vers la création de groupe de bâtiments.
 */
function onGoGroup(): void {
    onClose()
    router.push({ path: "/onboarding/group-building" })
}

/**
 * Cartes carrées (parcours d’ajout), alignées sur l’ancienne modal « options bâtiment ».
 */
const optionCards: BuildingOptionCard[] = [
    {
        label: "Ajout simple",
        iconType: "immeuble",
        onSelect: onGoSimple,
    },
    {
        label: "Ajout de plusieurs bâtiments et compteurs",
        iconType: "maison_appartement",
        onSelect: onGoBatch,
    },
    {
        label: "Groupe de bâtiments",
        iconType: "groupe",
        onSelect: onGoGroup,
    },
]
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOGUE — CHOIX DU PARCOURS D’AJOUT -->
    <!-- ================================ -->
    <BaseDialog
        :visible="modal.kind === 'buildingOptions'"
        modal
        header="Ajouter des bâtiments"
        class="w-[min(100%,40rem)]"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <p class="mb-4 text-sm text-surface-700">
            Choisissez un parcours d’ajout de bâtiment.
        </p>
        <div
            class="flex flex-wrap items-center justify-center gap-4 [--dashboard-building-option-size:9rem] md:[--dashboard-building-option-size:9.375rem]"
        >
            <div
                v-for="card in optionCards"
                :key="card.label"
                class="flex w-[calc(var(--dashboard-building-option-size)+0.625rem)] flex-col items-center justify-center"
            >
                <button
                    type="button"
                    class="flex size-[var(--dashboard-building-option-size)] max-h-[var(--dashboard-building-option-size)] max-w-[var(--dashboard-building-option-size)] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-surface-300 bg-surface-0 p-3 text-center transition-all hover:border-primary hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    @click="card.onSelect"
                >
                    <span
                        class="flex min-h-0 w-full flex-1 items-center justify-center"
                    >
                        <BaseBuildingIcon :type="card.iconType" :size="48" />
                    </span>
                    <span
                        class="mt-0.5 line-clamp-2 text-center text-xs leading-tight text-surface-700 md:text-sm"
                    >
                        {{ card.label }}
                    </span>
                </button>
            </div>
        </div>
        <template #footer>
            <BaseButton label="Fermer" severity="secondary" @click="onClose" />
        </template>
    </BaseDialog>
</template>
